import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@shopify/polaris';

/**
 * LanguageSwitcher Component
 * 
 * Provides a dropdown interface for users to switch between Portuguese (Brazil) and English.
 * Follows Shopify Polaris design guidelines and integrates with i18next for localization.
 * 
 * Features:
 * - Automatic language detection from browser/localStorage
 * - Persistent language preference storage
 * - Seamless UI updates without page reload
 * - Accessible dropdown with proper labels
 */
export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();

  const languageOptions = [
    { 
      label: '🇧🇷 Português (Brasil)', 
      value: 'pt',
      prefix: '🇧🇷'
    },
    { 
      label: '🇺🇸 English', 
      value: 'en',
      prefix: '🇺🇸'
    }
  ];

  const handleLanguageChange = (newLanguage) => {
    // Change language using i18next
    i18n.changeLanguage(newLanguage);
    
    // Store preference in localStorage for persistence
    localStorage.setItem('i18nextLng', newLanguage);
    
    // Optional: Send analytics or tracking data
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'language_change', {
        'custom_parameter': newLanguage
      });
    }
  };

  const getCurrentLanguageLabel = () => {
    const currentLang = languageOptions.find(lang => lang.value === i18n.language);
    return currentLang ? currentLang.label : languageOptions[0].label;
  };

  return (
    <div style={{ minWidth: '200px' }}>
      <Select
        label={t('common.language')}
        options={languageOptions}
        value={i18n.language}
        onChange={handleLanguageChange}
        placeholder={getCurrentLanguageLabel()}
      />
    </div>
  );
}

/**
 * Compact Language Switcher for navigation bars
 * Shows only flag icons to save space
 */
export function CompactLanguageSwitcher() {
  const { i18n } = useTranslation();

  const handleLanguageChange = (newLanguage) => {
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('i18nextLng', newLanguage);
  };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <button
        onClick={() => handleLanguageChange('pt')}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: '16px',
          opacity: i18n.language === 'pt' ? 1 : 0.5,
          transition: 'opacity 0.2s ease'
        }}
        title="Português (Brasil)"
        aria-label="Switch to Portuguese"
      >
        🇧🇷
      </button>
      <button
        onClick={() => handleLanguageChange('en')}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: '16px',
          opacity: i18n.language === 'en' ? 1 : 0.5,
          transition: 'opacity 0.2s ease'
        }}
        title="English"
        aria-label="Switch to English"
      >
        🇺🇸
      </button>
    </div>
  );
}

export default LanguageSwitcher;